import{useCallback,useMemo,useState}from"react";
import{deviceAuthorityApi,securityApi}from"../../lib/api";
import{passkeyType}from"../../lib/format";
import{runLoginCeremony}from"../../lib/webauthn";
import{useResource}from"../../lib/useResource";
import{EmptyState,InlineNotice,LoadingLine}from"../../components/State";
import{ResourceList,StateText,WorkspaceHeader}from"../../components/Foundation";

type PersonalTab="overview"|"passkeys"|"recovery";

function asRecord(v:unknown):Record<string,unknown>{
  return v&&typeof v==="object"&&!Array.isArray(v)?v as Record<string,unknown>:{};
}
function bool(v:unknown){return v===true}
function num(v:unknown){return typeof v==="number"?v:0}
function str(v:unknown,d=""){return typeof v==="string"?v:d}

async function signInWithPasskey(onSuccess:()=>void, onError:(msg:string)=>void){
  try{
    const options=await deviceAuthorityApi.loginOptions();
    const proof=await runLoginCeremony(options);
    await deviceAuthorityApi.loginVerify(proof);
    onSuccess();
  }catch(e){
    onError(e instanceof Error?e.message:"Passkey sign-in failed");
  }
}

export function PersonalSecurity({onOpenVault}:{onOpenVault:()=>void}){
  const resource=useResource(useCallback(securityApi.my,[]));
  const[tab,setTab]=useState<PersonalTab>("overview");
  const[signInError,setSignInError]=useState<string|null>(null);
  const data=resource.data;
  const me=asRecord(data?.me);
  const recovery=asRecord(data?.recovery);
  const passkeys=Array.isArray(data?.passkeys)?data!.passkeys.map(asRecord):[];
  const vaults=Array.isArray(data?.vaults)?data!.vaults:[];
  const name=str(me.display_name,str(me.name,"My Security"));
  const unlockCount=num(recovery.unlock_credentials||me.unlock_passkeys);
  const trustCount=num(recovery.trust_groups||me.trust_count);
  const belowUnlock=bool(recovery.below_unlock_minimum);
  const belowTrust=bool(recovery.below_trust_group_minimum);
  const needsAttention=belowUnlock||belowTrust;
  const statusTitle=needsAttention?"Protection needs attention":"Protected";

  const syncedPasskeys=useMemo(
   ()=>passkeys.filter(
    p=>bool(p.backed_up)||str(p.device_type).includes("MULTI_DEVICE")
   ).length,
   [passkeys]
  );

  void vaults;

  return <div className="page personal-security">
   <WorkspaceHeader
    eyebrow="My Security"
    title={name}
    description="Your sign-in protection, recovery and Personal Vault."
    actions={resource.status==="loading"?<LoadingLine/>:undefined}
   />

   {resource.status==="error"&&!resource.data&&
    <InlineNotice
     tone="attention"
     title="Passkey session required"
     detail="Sign in with an approved passkey to open your personal security context."
     action={
      <button
       className="button secondary"
       onClick={()=>{setSignInError(null);signInWithPasskey(()=>{resource.refresh();},setSignInError);}}
      >
       Sign in
      </button>
     }
    />
   }

   {signInError&&
    <InlineNotice
     tone="critical"
     title="Sign in failed"
     detail={signInError}
    />
   }

   {resource.status==="success"&&<>
    <div
     className="personal-tabs"
     role="tablist"
     aria-label="My Security"
    >
     <button
      className={tab==="overview"?"selected":""}
      onClick={()=>setTab("overview")}
      >
      Overview
     </button>

     <button
      className={tab==="passkeys"?"selected":""}
      onClick={()=>setTab("passkeys")}
     >
      Passkeys
     </button>

     <button
      className={tab==="recovery"?"selected":""}
      onClick={()=>setTab("recovery")}
     >
      Recovery
     </button>
    </div>

    {tab==="overview"&&
     <div className="personal-overview">
      <section
       className={`personal-status ${needsAttention?"attention":""}`}
      >
       <span className="section-kicker">
        Security status
       </span>

       <h2>{statusTitle}</h2>

       <p>
        {needsAttention
         ?"Your account is protected, but recovery can be made more resilient."
         :"Your current sign-in and recovery protection meets the configured minimums."}
       </p>
      </section>

      <div className="personal-grid">
       <section className="personal-resource">
        <span className="section-kicker">
         Passkeys
        </span>

        <h2>
         {passkeys.length} passkey{passkeys.length===1?"":"s"}
        </h2>

        <p>
         {syncedPasskeys
          ?`${syncedPasskeys} synced passkey · Available on supported devices.`
          :"Used to securely sign in and protect your account."}
        </p>

        <button
         className="personal-action"
         onClick={()=>setTab("passkeys")}
        >
         Manage passkeys →
        </button>
       </section>

       <section className="personal-resource">
        <span className="section-kicker">
         My Vault
        </span>

        <h2>Personal Vault</h2>

        <p>
         Your protected secrets remain isolated from organization administration.
        </p>

        <button
         className="personal-action"
         onClick={onOpenVault}
        >
         Open My Vault →
        </button>
       </section>

       <section className="personal-resource">
        <span className="section-kicker">
         Recovery
        </span>

        <h2>
         {needsAttention?"Needs attention":"Ready"}
        </h2>

        <p>
         {needsAttention
          ?`${Math.max(1,trustCount)} independent recovery method${trustCount===1?"":"s"} available.`
          :"Recovery protection meets current policy."}
        </p>

        <button
         className="personal-action"
         onClick={()=>setTab("recovery")}
        >
         Review recovery →
        </button>
       </section>
      </div>
     </div>
    }

    {tab==="passkeys"&&
     <section className="personal-detail">
      <div className="personal-detail-head">
       <div>
        <span className="section-kicker">
         Passkeys
        </span>

        <h2>Your passkeys</h2>

        <p>
         Passkeys protect sign-in and can also unlock your Personal Vault.
        </p>
       </div>

       <button
        className="button secondary"
        onClick={()=>{setSignInError(null);signInWithPasskey(()=>{resource.refresh();},setSignInError);}}
       >
        Add or remove passkeys
       </button>
      </div>

      <ResourceList label="Your passkeys">
       <div className="personal-list">
        {passkeys.map((p,i)=>{
         const status=str(p.status,"Active");
         const active=status.toUpperCase()==="ACTIVE";

         return <div
          className="personal-row"
          key={str(p.id,`pk-${i}`)}
         >
          <div>
           <strong>
            {str(p.label,"Passkey")}
           </strong>

           <span>
            {passkeyType(
             str(p.device_type),
             bool(p.backed_up)
            )}
           </span>
          </div>

          <div className="personal-row-meta">
           <span>
            {bool(p.unlock_capable)
             ?"Can unlock vault"
             :"Sign-in only"}
           </span>

           <StateText
            tone={active?"positive":"neutral"}
           >
            {status.toLowerCase()==="active"
             ?"Active"
             :status}
           </StateText>
          </div>
         </div>
        })}

        {!passkeys.length&&
         <EmptyState
          title="No passkeys"
          detail="Add a passkey to protect sign-in and recovery."
         />
        }
       </div>
      </ResourceList>

      <p className="personal-help">
       For stronger recovery, add a passkey from another device or provider.
      </p>
     </section>
    }

    {tab==="recovery"&&
     <section className="personal-detail">
      <div className="personal-detail-head">
       <div>
        <span className="section-kicker">
         Recovery
        </span>

        <h2>
         {needsAttention
          ?"Improve recovery protection"
          :"Recovery protection is ready"}
        </h2>

        <p>
         Recovery strength depends on independent ways to regain access,
         not just the number of passkeys.
        </p>
       </div>
      </div>

      <div className="recovery-summary">
       <div>
        <span>
         Vault-unlocking passkeys
        </span>
        <strong>{unlockCount}</strong>
       </div>

       <div>
        <span>
         Independent recovery methods
        </span>
        <strong>{trustCount}</strong>
       </div>

       <div>
        <span>
         Offline recovery
        </span>

        <strong>
         <StateText
          tone={
           bool(recovery.offline_recovery_ready)
            ?"positive"
            :"attention"
          }
         >
          {bool(recovery.offline_recovery_ready)
           ?"Ready"
           :"Not ready"}
         </StateText>
        </strong>
       </div>
      </div>

      {needsAttention?
       <div className="recovery-actions">
        {belowUnlock&&
         <div>
          <strong>
           Add another vault-unlocking passkey
          </strong>

          <p>
           A second passkey that can unlock your vault reduces the chance
           of losing access.
          </p>
         </div>
        }

        {belowTrust&&
         <div>
          <strong>
           Add an independent recovery method
          </strong>

          <p>
           Use another device or provider so one device or provider is not
           your only recovery path.
          </p>
         </div>
        }

        <button
         className="button secondary"
         onClick={()=>{setSignInError(null);signInWithPasskey(()=>{resource.refresh();},setSignInError);}}
        >
         Improve recovery
        </button>
       </div>
       :
       <p className="quiet-copy">
        No recovery action is currently required.
       </p>
      }
     </section>
    }
   </>}

   {resource.status==="success"&&!data?.me&&
    <EmptyState
     title="Personal security context is empty"
     detail="No personal security identity was returned for this session."
    />
   }
  </div>
}
